import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { alert } from 'actions/flash'
import { useUpdateMerchantMutation } from 'api/merchants'
import { isError } from 'api/utils'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import {
  markFormPersisted,
  setFormSubmitting,
} from 'slices/orderFeedbackGuestExperience'
import {
  selectAcknowledgmentTooLong,
  selectApiFormattedFormData,
  selectFormDisabled,
  selectIsFormDirty,
  selectIsFormSubmitting,
} from 'slices/orderFeedbackGuestExperience/selectors'
import { useSettingsEditPermission } from './useSettingsEditPermission'

const t = buildTranslate('order_ratings.settings.guest_experience')

export const useGuestExperienceSubmitForm = () => {
  const dispatch = useDispatch()
  const apiFormattedFormData = useSelector(selectApiFormattedFormData)
  const formDisabled = useSelector(selectFormDisabled)
  const isFormDirty = useSelector(selectIsFormDirty)
  const isFormSubmitting = useSelector(selectIsFormSubmitting)
  const acknowledgmentTooLong = useSelector(selectAcknowledgmentTooLong)
  const [updateMerchant] = useUpdateMerchantMutation()
  // FR-60. Presentation only: the server already carries
  // `rbac_authorize!('manage', 'merchant_settings')` on PATCH /merchants/:id.
  // This keeps a call the viewer cannot make off the wire.
  const { editDisabled } = useSettingsEditPermission()

  const handleSubmit = useCallback(async () => {
    // Guard the handler too, not just the button. `acknowledgmentTooLong` is
    // here for the same reason: a bypassed disabled prop (or a value that
    // predates this pane's own maxLength) must still not be able to publish a
    // message the server would reject.
    if (
      editDisabled ||
      formDisabled ||
      isFormSubmitting ||
      !isFormDirty ||
      acknowledgmentTooLong
    )
      return

    dispatch(setFormSubmitting(true))
    try {
      const result = await updateMerchant({
        data: apiFormattedFormData,
      })

      if (isError(result)) {
        dispatch(
          alert({
            key: 'danger',
            message: t('alert.submit_error'),
            timeout: 5,
          })
        )
        return
      }

      // The edit is now the persisted value, so the pane goes clean and
      // Publish disables again until the next change.
      dispatch(markFormPersisted())
      dispatch(
        alert({
          key: 'success',
          message: t('alert.submit_success'),
          timeout: 5,
        })
      )
    } finally {
      // Always clear isFormSubmitting — covers the normal isError path AND a
      // thrown error from any middleware in the dispatch chain, not just the
      // normal error path.
      dispatch(setFormSubmitting(false))
    }
  }, [
    apiFormattedFormData,
    updateMerchant,
    dispatch,
    editDisabled,
    formDisabled,
    isFormDirty,
    isFormSubmitting,
    acknowledgmentTooLong,
  ])

  return handleSubmit
}
